import { Component } from '@theme/component';
import { clamp, prefersReducedMotion } from '@theme/utilities';

/**
 * Video carousel — a vertical "rolodex" of video cards with a full-screen cinema mode.
 *
 * Ported from the Paris Hilton "Full Bleed" prototype's standalone
 * `apps/video-carousel/src/main.js`. Per LIQUID-MIGRATION.md the carousel ships as an
 * in-page section, never an iframe route, so the prototype's
 * `parent.postMessage({ type: 'paris-video-carousel-cinema' })` bridge has been removed.
 *
 * ---------------------------------------------------------------------------
 * CINEMA-MODE EVENT CONTRACT (for homepage-showcase.js and anything else on the page)
 * ---------------------------------------------------------------------------
 * Event name: 'ph:carousel-cinema'
 * Dispatched on: the <video-carousel-component> element.
 * bubbles: true, composed: true — so `document.addEventListener('ph:carousel-cinema', …)`
 * or a listener on the showcase root both work.
 *
 *   detail = {
 *     active:    boolean,          // true when cinema opens, false when it closes
 *     index:     number,           // index of the card in cinema, or -1 when closed
 *     sectionId: string,           // the Shopify section id that owns this carousel
 *   }
 *
 * The shared showcase controller should pause its wheel-hijacking / horizontal
 * scroll-snapping while `detail.active === true`, and resume on the matching
 * `active === false` event. A closing event is also dispatched from
 * `disconnectedCallback`, so a controller can never be left stuck in the paused state
 * if the section is removed or re-rendered by the theme editor while cinema is open.
 * ---------------------------------------------------------------------------
 *
 * Wheel handling: this component hijacks the wheel anywhere over the panel once the
 * showcase rail has fully scrolled it into view, stopping propagation so the showcase
 * controller's horizontal scroll does not also fire. Forward scroll advances the
 * rolodex; backward scroll is never applied to the rolodex — it's left to bubble up so
 * the showcase controller scrolls back to the previous homepage panel instead. Under
 * `prefers-reduced-motion` the wheel is not hijacked at all — the prev/next buttons,
 * arrow keys, drag and click all still work.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [stage]
 * @property {HTMLElement} [stack]
 * @property {HTMLInputElement} [playbar]
 * @property {HTMLElement} [metaLabel]
 * @property {HTMLElement} [metaTitle]
 * @property {HTMLButtonElement} [cinemaClose]
 *
 * @extends Component<Refs>
 */
export class VideoCarouselComponent extends Component {
  /** @type {HTMLElement[]} */
  #cards = [];
  /** Fractional scroll position of the rolodex, in card units. */
  #position = 0;
  /** Where #position is lerping towards. */
  #target = 0;
  /** Card currently centered (drives the meta text and muted preview). */
  #activeIndex = -1;
  /** Card currently in cinema mode, or -1. */
  #selectedIndex = -1;
  /** Card that should enter cinema mode once it finishes centering. */
  #pendingCinemaIndex = -1;
  /** @type {number | null} */
  #frame = null;
  /** @type {number} */
  #settleTimer = 0;
  /** @type {number} */
  #cinemaCloseTimer = 0;
  #dragging = false;
  #suppressClick = false;
  #scrubbing = false;
  #pointerStart = 0;
  #pointerOrigin = 0;
  /** Per-video rAF ids for the audio fade tween. @type {WeakMap<HTMLVideoElement, number>} */
  #volumeFrames = new WeakMap();
  /** @type {AbortController | null} */
  #dragAbort = null;
  /** @type {AbortController | null} */
  #cinemaAbort = null;
  /** @type {AbortController | null} */
  #scrubAbort = null;
  /** @type {AbortController | null} */
  #lifetimeAbort = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  // Pure width check, matching the rest of the showcase's mobile/desktop
  // split (see header.js's COMPACT_QUERY for why touch/hover isn't used).
  #compact = matchMedia('(max-width: 989px)');
  #inView = true;

  connectedCallback() {
    super.connectedCallback();

    // Cards are tracked in a stable array rather than through `ref="cards[]"`: cinema
    // mode moves the selected card out of the stack and into the stage, which would
    // reshuffle a DOM-order refs array and break every index we hold.
    this.#cards = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.ph-vc__card')]);
    if (!this.#cards.length || !this.refs.stack) return;

    this.#lifetimeAbort = new AbortController();
    const { signal } = this.#lifetimeAbort;

    for (const card of this.#cards) {
      const poster = card.querySelector('img');
      const reveal = () => card.classList.add('is-media-ready');
      if (!poster || poster.complete) reveal();
      else poster.addEventListener('load', reveal, { once: true, signal });

      const video = this.#videoOf(card);
      if (!video) continue;
      video.volume = 0;
      video.muted = true;
      video.addEventListener('timeupdate', () => {
        if (this.#cards.indexOf(card) === this.#selectedIndex) this.#updatePlaybar(video);
      }, { signal });
      video.addEventListener('ended', () => this.#handleVideoEnded(card), { signal });
    }

    // Wheel is bound to the stage, but only acted on when the pointer is actually over
    // the card stack, so the empty margins keep falling through to the showcase
    // controller. `passive: false` because we preventDefault.
    this.refs.stage?.addEventListener('wheel', this.#handleWheel, { signal, passive: false });

    // Pause everything while the panel is off-screen — the prototype was a whole page,
    // this one is a single panel in a long horizontal reel.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#inView = entry.isIntersecting;
          if (!entry.isIntersecting) {
            this.#exitCinema(true);
            for (const card of this.#cards) this.#videoOf(card)?.pause();
          } else {
            this.#syncPlayback();
          }
        }
      },
      { threshold: 0.05 }
    );
    this.#observer.observe(this);

    // Theme editor: focus the card a merchant selects in the sidebar.
    this.addEventListener('shopify:block:select', (event) => {
      const card = /** @type {HTMLElement} */ (event.target)?.closest?.('.ph-vc__card');
      if (card) this.#goToCard(this.#cards.indexOf(card));
    }, { signal });

    this.#draw();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Never leave the showcase controller paused on a section that no longer exists.
    if (this.#selectedIndex >= 0) this.#notifyCinema(false, this.#selectedIndex);

    this.#lifetimeAbort?.abort();
    this.#dragAbort?.abort();
    this.#cinemaAbort?.abort();
    this.#scrubAbort?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#settleTimer);
    clearTimeout(this.#cinemaCloseTimer);
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = null;
  }

  /* ------------------------------------------------------------------ *
   * Declarative handlers (wired through `on:*` attributes in the Liquid)
   * ------------------------------------------------------------------ */

  /**
   * A click landing on a card. Centers it, or opens cinema mode if it is already centered.
   *
   * `stopPropagation()` matters here: the rolodex's cards are a 3D stack of overlapping
   * rectangles (each just offset/scaled from its neighbours, not clipped to a
   * non-overlapping slot), so a click clearly intended for one card — say, the one just
   * below the centered one — very often *also* falls inside the centered card's own
   * bounding box. Without stopping it here, the click would go on to reach
   * `handleStageClick` too, whose "inside the active card's box" fallback below exists for
   * a different case (a genuine backdrop click near the active card) but can't tell that
   * apart from this one — and would re-activate the *active* card instead, opening cinema
   * for it even though the click actually landed on a different, uncentered card.
   * @param {PointerEvent} event
   */
  handleCardClick(event) {
    if (this.#consumeDragClick()) return;
    event.stopPropagation();
    const card = /** @type {HTMLElement} */ (event.target);
    const index = this.#cards.indexOf(card);
    if (index >= 0) this.#activateCard(index);
  }

  /**
   * A click on the stage but not on any card (handleCardClick above claims and stops
   * propagation for anything that lands on one): closes cinema mode, or — matching the
   * prototype — still counts as a hit if it lands inside the centered card's box, for a
   * backdrop click that's near enough to the active card to reasonably count as hitting it.
   * @param {PointerEvent} event
   */
  handleStageClick(event) {
    if (this.#consumeDragClick()) return;
    if (this.#selectedIndex >= 0) {
      this.#exitCinema();
      return;
    }
    if (this.#activeIndex < 0) return;
    const bounds = this.#cards[this.#activeIndex].getBoundingClientRect();
    const inside =
      event.clientX >= bounds.left &&
      event.clientX <= bounds.right &&
      event.clientY >= bounds.top &&
      event.clientY <= bounds.bottom;
    if (inside) this.#activateCard(this.#activeIndex);
  }

  /**
   * Starts a drag. pointermove/pointerup are bound imperatively because the declarative
   * layer only delegates `pointerdown`.
   * @param {PointerEvent} event
   */
  handlePointerDown(event) {
    const { stack } = this.refs;
    if (!stack || this.#selectedIndex >= 0) return;

    clearTimeout(this.#settleTimer);
    this.#settleTimer = 0;
    this.#dragging = true;
    this.#suppressClick = false;
    this.#pointerStart = event.clientY;
    this.#pointerOrigin = event.clientY;
    stack.classList.add('is-dragging');

    this.#dragAbort?.abort();
    this.#dragAbort = new AbortController();
    const { signal } = this.#dragAbort;

    stack.addEventListener('pointermove', /** @param {PointerEvent} moveEvent */ (moveEvent) => {
      if (!this.#dragging) return;
      if (Math.abs(moveEvent.clientY - this.#pointerOrigin) > 5) {
        this.#suppressClick = true;
        if (!stack.hasPointerCapture(moveEvent.pointerId)) stack.setPointerCapture(moveEvent.pointerId);
      }
      this.#moveBy((this.#pointerStart - moveEvent.clientY) * (this.#compact.matches ? 0.013 : 0.0065));
      this.#pointerStart = moveEvent.clientY;
    }, { signal });

    const endDrag = () => {
      this.#dragging = false;
      if (this.#suppressClick) this.#scheduleCenter();
      stack.classList.remove('is-dragging');
      this.#dragAbort?.abort();
      this.#dragAbort = null;
    };

    stack.addEventListener('pointerup', endDrag, { signal });
    stack.addEventListener('pointercancel', endDrag, { signal });
  }

  /**
   * Arrow keys move the rolodex, Enter/Space opens the focused card, Escape leaves cinema.
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    // `Component` proxies `event.target` to the element carrying the `on:keydown`
    // attribute (this host), so reach for the real target through composedPath().
    const origin = /** @type {HTMLElement | undefined} */ (event.composedPath?.()[0]);

    if (origin === this.refs.playbar) {
      // Let the range input own its own arrow keys for scrubbing.
      if (event.key === 'Escape') this.#exitCinema();
      return;
    }

    const card = origin?.closest?.('.ph-vc__card');
    if (card && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.#activateCard(this.#cards.indexOf(/** @type {HTMLElement} */ (card)));
      return;
    }

    switch (event.key) {
      case 'Escape':
        this.#exitCinema();
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        this.#moveBy(1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        this.#moveBy(-1);
        break;
      default:
        break;
    }
  }

  /**
   * Grab the playbar. Also stops the press reaching the rolodex's drag handler, because
   * the declarative delegation resolves `pointerdown` to the nearest annotated element.
   */
  startScrub() {
    this.#scrubbing = true;
    this.#scrubAbort?.abort();
    this.#scrubAbort = new AbortController();
    const release = () => {
      this.#scrubbing = false;
      this.#scrubAbort?.abort();
      this.#scrubAbort = null;
    };
    document.addEventListener('pointerup', release, { signal: this.#scrubAbort.signal });
    document.addEventListener('pointercancel', release, { signal: this.#scrubAbort.signal });
  }

  /** Scrub the cinema video from the playbar. */
  handleScrub() {
    const { playbar } = this.refs;
    if (!playbar) return;
    const video = this.#selectedVideo();
    if (video?.duration) video.currentTime = (video.duration * Number(playbar.value)) / 100;
    playbar.style.setProperty('--progress', `${playbar.value}%`);
  }

  /** Close button in cinema mode. */
  closeCinema() {
    this.#exitCinema();
  }

  /**
   * No-op used by the playbar so the declarative delegation resolves to it instead of
   * bubbling to the stage's "click the backdrop to close" handler.
   */
  swallowEvent() {}

  /* ------------------------------------------------------------------ *
   * Rolodex motion
   * ------------------------------------------------------------------ */

  /** @param {WheelEvent} event */
  #handleWheel = (event) => {
    // Cinema mode is modal: swallow the wheel entirely so the page behind the overlay
    // does not scroll out from under the video.
    if (this.#selectedIndex >= 0) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (prefersReducedMotion()) return;

    // Don't take over the wheel until the showcase rail has actually finished
    // bringing this panel fully into view — otherwise the rolodex starts eating
    // scroll input mid-transition, before the panel has even arrived.
    if (!this.#isFullyInView()) return;

    // Backward scroll always hands the wheel back to the showcase rail (which
    // scrolls to the previous homepage panel) instead of moving the rolodex
    // backward — the rolodex only ever advances forward here; reversing is
    // "leave the panel", not "rewind the reel".
    if (event.deltaY < 0) return;

    event.preventDefault();
    // Keep the showcase controller's horizontal wheel hijack from also firing.
    event.stopPropagation();
    this.#moveBy(event.deltaY * 0.0024);
  };

  /** True once this panel is fully scrolled into place in the showcase rail — not mid-transition. */
  #isFullyInView() {
    const rect = this.getBoundingClientRect();
    return Math.abs(rect.left) < 1 && Math.abs(rect.top) < 1;
  }

  /** @param {number} delta */
  #moveBy(delta) {
    this.#exitCinema();
    this.#target += delta;
    this.#requestDraw();
    this.#scheduleCenter();
  }

  /** @param {number} index */
  #goToCard(index) {
    this.#target = this.#position + this.#wrappedDelta(index, this.#position);
    this.#requestDraw();
  }

  /**
   * A click on a card either brings it to center or, if it's already
   * centered, opens cinema mode.
   *
   * The prototype (and this component's own first port of it) queues cinema
   * mode to auto-open as soon as the settle animation finishes when a click
   * lands on the *active* card before it's fully centered — e.g. clicking a
   * card that's already nearest-to-center but still mid-drag-settle. In
   * practice that reads as a bug, not a feature: the click doesn't seem to
   * do anything for a beat, then cinema mode opens on its own a moment
   * later with no further input — easy to mistake for an unrelated click
   * (or a second, now-mistimed click on what has just become the cinema
   * overlay) immediately closing it again. This intentionally drops that
   * auto-open queuing: a click that isn't yet on a stably centered card just
   * centers it, full stop — cinema mode only opens from a click that lands
   * while the card is already centered.
   * @param {number} index
   */
  #activateCard(index) {
    if (index < 0) return;
    if (index !== this.#activeIndex) {
      this.#goToCard(index);
      return;
    }
    // "Centered" is whether the settle animation has actually finished — `#frame` is
    // only ever non-null while `#draw()` still has a pending frame queued (its own exit
    // condition, `Math.abs(target - position) > 0.001`, is what clears it) — rather than
    // re-deriving the same thing from position math against a fixed epsilon a second
    // time here. The exponential ease this rolodex uses only ever *approaches* its
    // target asymptotically, so re-checking an epsilon this tight against `#position`
    // independently is fragile: `#draw()` can have already legitimately stopped
    // scheduling more frames while still sitting a hair outside that same tolerance,
    // which left clicking a visually-settled, genuinely active card doing nothing.
    if (this.#frame === null) this.#selectCard(index);
  }

  /** Snap to the nearest whole card once the user stops nudging. */
  #scheduleCenter() {
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => {
      this.#settleTimer = 0;
      this.#target = Math.round(this.#target);
      this.#requestDraw();
    }, 180);
  }

  #requestDraw() {
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#draw);
  }

  #draw = () => {
    const count = this.#cards.length;
    if (!count) return;

    const reduced = prefersReducedMotion();
    const ease = reduced ? 1 : this.#compact.matches ? 0.18 : 0.095;
    this.#position += (this.#target - this.#position) * ease;

    const nearest = ((Math.round(this.#position) % count) + count) % count;
    this.#setCurrent(nearest);

    const spacing = this.#compact.matches
      ? Math.max(74, window.innerHeight * 0.13)
      : Math.max(104, window.innerHeight * 0.17);

    for (const [index, card] of this.#cards.entries()) {
      const delta = this.#wrappedDelta(index, this.#position);
      const distance = Math.abs(delta);
      const scale = Math.max(0.7, 0.94 - distance * 0.045);
      card.style.zIndex = String(50 - Math.round(distance * 5));
      card.style.opacity = distance > 5.1 ? '0' : String(Math.max(0.58, 1 - distance * 0.06));
      card.style.transformOrigin = '50% 50%';
      card.style.transform = `translate3d(0, ${delta * spacing}px, ${-distance * 72}px) scale(${scale})`;
      card.style.pointerEvents = distance < 5.1 ? 'auto' : 'none';
    }

    if (Math.abs(this.#target - this.#position) > 0.001) {
      this.#frame = requestAnimationFrame(this.#draw);
      return;
    }

    this.#frame = null;
    if (
      this.#pendingCinemaIndex === this.#activeIndex &&
      Math.abs(this.#wrappedDelta(this.#activeIndex, this.#position)) < 0.001
    ) {
      this.#selectCard(this.#pendingCinemaIndex);
    }
  };

  /**
   * Shortest signed distance from `value` to `index` around the loop.
   * @param {number} index
   * @param {number} value
   */
  #wrappedDelta(index, value) {
    const count = this.#cards.length;
    let delta = index - value;
    const half = count / 2;
    while (delta > half) delta -= count;
    while (delta < -half) delta += count;
    return delta;
  }

  /* ------------------------------------------------------------------ *
   * Active card / playback
   * ------------------------------------------------------------------ */

  /** @param {number} index */
  #setCurrent(index) {
    if (index === this.#activeIndex) return;
    this.#activeIndex = index;

    const card = this.#cards[index];
    const { metaLabel, metaTitle } = this.refs;
    if (metaLabel) metaLabel.textContent = card.dataset.label ?? '';
    if (metaTitle) metaTitle.textContent = card.dataset.title ?? '';

    for (const [cardIndex, entry] of this.#cards.entries()) {
      const active = cardIndex === index;
      // Warm up the neighbours so a click into cinema mode is not a cold start.
      if (Math.abs(this.#wrappedDelta(cardIndex, index)) <= 1) this.#prepareVideo(entry);
      entry.classList.toggle('is-active', active);
      entry.setAttribute('aria-hidden', String(!active));
      entry.tabIndex = active ? 0 : -1;
    }

    this.#syncPlayback();
  }

  /** Play the centered card's muted preview and the cinema video; pause everything else. */
  #syncPlayback() {
    const previewsAllowed =
      this.dataset.previewAutoplay !== 'false' && !prefersReducedMotion() && this.#inView;

    for (const [index, card] of this.#cards.entries()) {
      const video = this.#videoOf(card);
      if (!video) continue;
      // The cinema video must keep playing regardless of `#activeIndex` — it's a
      // completely separate state from the rolodex's own "which card is centered"
      // tracking, and re-checking activeIndex here previously caused this component's
      // own IntersectionObserver (below) to pause the cinema video the moment it
      // re-ran `#syncPlayback()`, even while nothing had actually scrolled out of view.
      const shouldPlay = index === this.#selectedIndex || (index === this.#activeIndex && previewsAllowed);
      if (shouldPlay) {
        video.play().then(() => card.classList.add('is-playing')).catch(() => {});
      } else {
        video.pause();
        card.classList.remove('is-playing');
      }
    }

    this.#syncAudio();
  }

  /** @param {HTMLElement} card */
  #prepareVideo(card) {
    const video = this.#videoOf(card);
    if (!video || video.dataset.phPrepared === 'true') return;
    video.dataset.phPrepared = 'true';
    // Rendered by `video_tag` with preload="none" and <source> children, so flipping
    // preload and calling load() is what actually starts fetching.
    video.preload = 'auto';
    video.load();
  }

  /** @param {HTMLElement} card */
  #videoOf(card) {
    return /** @type {HTMLVideoElement | null} */ (card.querySelector('video'));
  }

  #selectedVideo() {
    return this.#selectedIndex >= 0 ? this.#videoOf(this.#cards[this.#selectedIndex]) : null;
  }

  /**
   * Cinema mode is the only place audio plays, and it fades in rather than snapping on.
   */
  #syncAudio() {
    for (const [index, card] of this.#cards.entries()) {
      const video = this.#videoOf(card);
      if (!video) continue;
      this.#animateVolume(video, index === this.#selectedIndex && index === this.#activeIndex ? 1 : 0);
    }
  }

  /**
   * rAF volume tween — ported from the prototype's `animateVolume`.
   * @param {HTMLVideoElement} video
   * @param {number} targetVolume
   */
  #animateVolume(video, targetVolume) {
    const previous = this.#volumeFrames.get(video);
    if (previous) cancelAnimationFrame(previous);

    const startVolume = video.volume;
    if (targetVolume) video.muted = false;

    if (Math.abs(targetVolume - startVolume) < 0.01 || prefersReducedMotion()) {
      this.#volumeFrames.delete(video);
      video.volume = targetVolume;
      if (!targetVolume) video.muted = true;
      return;
    }

    const startedAt = performance.now();
    /** @param {number} now */
    const tick = (now) => {
      const progress = Math.min(1, (now - startedAt) / 800);
      const eased = 1 - (1 - progress) ** 3;
      video.volume = clamp(startVolume + (targetVolume - startVolume) * eased, 0, 1);
      if (progress < 1) {
        this.#volumeFrames.set(video, requestAnimationFrame(tick));
        return;
      }
      this.#volumeFrames.delete(video);
      if (!targetVolume) video.muted = true;
    };

    this.#volumeFrames.set(video, requestAnimationFrame(tick));
  }

  /** @param {HTMLVideoElement | null} video */
  #updatePlaybar(video) {
    const { playbar } = this.refs;
    if (!playbar) return;
    const progress = video?.duration ? Math.min(100, (video.currentTime / video.duration) * 100) : 0;
    if (!this.#scrubbing) playbar.value = String(progress);
    playbar.style.setProperty('--progress', `${this.#scrubbing ? playbar.value : progress}%`);
  }

  /**
   * When a cinema video finishes, roll on to the next one — same as the prototype.
   * @param {HTMLElement} card
   */
  #handleVideoEnded(card) {
    const index = this.#cards.indexOf(card);
    if (index !== this.#selectedIndex) return;

    const video = this.#videoOf(card);
    const nextIndex = (index + 1) % this.#cards.length;
    const nextVideo = this.#videoOf(this.#cards[nextIndex]);

    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    if (nextVideo?.readyState) nextVideo.currentTime = 0;

    this.#exitCinema(true);
    this.#pendingCinemaIndex = nextIndex;
    this.#goToCard(nextIndex);
  }

  /* ------------------------------------------------------------------ *
   * Cinema mode
   * ------------------------------------------------------------------ */

  /** @param {number} index */
  #selectCard(index) {
    this.#pendingCinemaIndex = -1;
    if (index < 0 || this.#selectedIndex === index) {
      this.#exitCinema();
      return;
    }
    if (this.#selectedIndex >= 0) this.#exitCinema(true);

    this.refs.stage?.classList.remove('is-cinema-closing');
    this.#applySelection(index);
    this.#notifyCinema(true, index);

    // Close cinema when the visitor clicks anywhere outside this section.
    this.#cinemaAbort = new AbortController();
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(/** @type {Node} */ (event.target))) this.#exitCinema();
    }, { signal: this.#cinemaAbort.signal, capture: true });

    requestAnimationFrame(() => this.refs.cinemaClose?.focus({ preventScroll: true }));
  }

  /**
   * @param {boolean} [immediate] - Skip the close animation.
   */
  #exitCinema(immediate = prefersReducedMotion()) {
    this.#pendingCinemaIndex = -1;
    const stage = this.refs.stage;
    if (this.#selectedIndex < 0 || !stage || stage.classList.contains('is-cinema-closing')) return;

    const index = this.#selectedIndex;
    const card = this.#cards[index];
    const video = this.#videoOf(card);
    const restoreFocus = document.activeElement === this.refs.cinemaClose;

    this.#cinemaAbort?.abort();
    this.#cinemaAbort = null;

    if (video) {
      video.muted = true;
      video.volume = 0;
    }

    /** @param {AnimationEvent} [event] */
    const finish = (event) => {
      if (event && event.animationName !== 'ph-vc-cinema-close') return;
      card.removeEventListener('animationend', /** @type {EventListener} */ (finish));
      clearTimeout(this.#cinemaCloseTimer);
      this.#cinemaCloseTimer = 0;
      if (this.#selectedIndex !== index) return;
      stage.classList.remove('is-cinema-closing');
      this.#applySelection(-1);
      this.#notifyCinema(false, index);
      if (restoreFocus) card.focus({ preventScroll: true });
    };

    if (immediate) {
      finish();
      return;
    }

    stage.classList.add('is-cinema-closing');
    card.addEventListener('animationend', /** @type {EventListener} */ (finish));
    this.#cinemaCloseTimer = setTimeout(finish, 500);
  }

  /**
   * Moves the selected card between the 3D stack and the flat stage. The card has to
   * leave the stack because the rolodex sets `perspective`, which would otherwise be
   * the containing block for the card's `position: fixed` cinema layout.
   * @param {number} index
   */
  #applySelection(index) {
    const previousIndex = this.#selectedIndex;
    const { stage, stack, playbar } = this.refs;
    this.#selectedIndex = index;

    if (index >= 0) {
      stage?.append(this.#cards[index]);
    } else if (previousIndex >= 0 && stack) {
      // Put the card back in source order rather than at the end of the stack.
      const nextCard = this.#cards.slice(previousIndex + 1).find((card) => card.parentElement === stack);
      stack.insertBefore(this.#cards[previousIndex], nextCard ?? null);
    }

    stage?.classList.toggle('is-cinema', index >= 0);

    for (const [cardIndex, card] of this.#cards.entries()) {
      const selected = cardIndex === index;
      card.classList.toggle('is-selected', selected);
      card.setAttribute('aria-pressed', String(selected));
      const video = this.#videoOf(card);
      if (video) video.loop = !selected;
    }

    const selectedVideo = this.#selectedVideo();
    if (playbar) {
      playbar.setAttribute('aria-hidden', String(!selectedVideo));
      playbar.tabIndex = selectedVideo ? 0 : -1;
    }
    if (this.refs.cinemaClose) this.refs.cinemaClose.tabIndex = index >= 0 ? 0 : -1;

    this.#updatePlaybar(selectedVideo);
    this.#syncPlayback();
  }

  /**
   * @param {boolean} active
   * @param {number} index
   */
  #notifyCinema(active, index) {
    // Lets CSS hide the header while a card is enlarged (see
    // component-homepage-showcase.css). Raising this section's z-index alone
    // isn't enough on some iOS Safari versions, which keep painting the
    // header's frosted (backdrop-filter) bar over higher layers.
    document.body.classList.toggle('ph-carousel-cinema', active);

    // A disconnected element cannot bubble to document listeners, so the teardown
    // "cinema closed" signal is dispatched straight onto the document instead. Either
    // way the showcase controller sees exactly one closing event.
    const target = this.isConnected ? this : document;
    target.dispatchEvent(
      new CustomEvent('ph:carousel-cinema', {
        bubbles: true,
        composed: true,
        detail: {
          active,
          index: active ? index : -1,
          sectionId: this.dataset.sectionId ?? this.id,
        },
      })
    );
  }

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */

  /** A drag that ended over a card should not also count as a click. */
  #consumeDragClick() {
    if (!this.#suppressClick) return false;
    this.#suppressClick = false;
    return true;
  }
}

if (!customElements.get('video-carousel-component')) {
  customElements.define('video-carousel-component', VideoCarouselComponent);
}
